import React, { useState, useEffect } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { LOGO_URL } from "@/lib/api";
import { useSettings, digitsOnly } from "@/contexts/SettingsContext";
import { useContent } from "@/contexts/ContentContext";

const ALL_ITEMS = [
  { to: "/", label: "Home", key: null },
  { to: "/about", label: "About", key: "about" },
  { to: "/doctors", label: "Doctors", key: "doctors" },
  { to: "/specialties", label: "Specialties", key: "specialties" },
  { to: "/services", label: "Services", key: "services" },
  { to: "/packages", label: "Packages", key: "packages" },
  { to: "/insurance", label: "Insurance", key: "insurance" },
  { to: "/schemes", label: "Schemes", key: "schemes" },
  { to: "/resources", label: "Resources", key: "resources" },
  { to: "/gallery", label: "Gallery", key: "gallery" },
  { to: "/careers", label: "Careers", key: "careers" },
  { to: "/contact", label: "Contact", key: "contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const s = useSettings();
  const c = useContent();
  const nav = c.nav || {};
  const emergencyTel = `tel:${digitsOnly(s.emergency_phone)}`;
  const navItems = ALL_ITEMS.filter((n) => n.key === null || nav[n.key] !== false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header className={`sticky top-0 z-40 transition-all duration-300 ${scrolled ? "glass-nav" : "bg-white border-b border-slate-100"}`} data-testid="site-navbar">
      <div className="bg-slate-50 text-[11px] hidden md:block border-b border-slate-100">
        <div className="container-x flex justify-between py-1.5">
          <span className="text-mute tracking-wide uppercase">A Unit of Hanshi Hospitals Pvt. Ltd.</span>
          <span className="flex items-center gap-5">
            <a href={emergencyTel} className="flex items-center gap-1.5 text-red-600 hover:text-red-700 font-semibold" data-testid="topbar-emergency-link">
              <Phone className="h-3 w-3" /> Emergency: {s.emergency_phone}
            </a>
            <Link to="/admin/login" className="text-mute hover:text-brand-purple" data-testid="topbar-admin-link">Admin</Link>
          </span>
        </div>
      </div>
      <div className="container-x flex items-center justify-between py-3 gap-4">
        <Link to="/" className="flex items-center shrink-0" data-testid="navbar-logo-link" aria-label="One Hospitals home">
          <img src={LOGO_URL} alt="One Hospitals — Your health, One destination" className="h-12 sm:h-14 w-auto object-contain shrink-0" />
        </Link>

        <nav className="hidden xl:flex items-center gap-0.5 ml-auto">
          {navItems.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.to === "/"}
              className={({ isActive }) => `px-3 py-2 text-[13px] font-medium rounded-full transition ${isActive ? "text-brand-purple bg-brand-purple/10" : "text-slate-600 hover:text-brand-purple hover:bg-slate-50"}`}
              data-testid={`nav-${n.label.toLowerCase()}`}>
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-2 ml-2 shrink-0">
          {nav.second_opinion_btn !== false && <Link to="/second-opinion" className="btn-outline text-xs !py-2 !px-4" data-testid="navbar-second-opinion-btn">Second Opinion</Link>}
          {nav.book_btn !== false && <Link to="/book-appointment" className="btn-primary text-xs !py-2 !px-4" data-testid="navbar-book-appointment-btn">Book Now</Link>}
        </div>
        <button className="xl:hidden p-2 text-slate-700" onClick={() => setOpen((v) => !v)} aria-label="Toggle menu" data-testid="navbar-menu-toggle">
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>
      {open && (
        <div className="xl:hidden bg-white border-t border-slate-100" data-testid="mobile-nav">
          <div className="container-x py-3 grid grid-cols-2 gap-1">
            {navItems.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.to === "/"} onClick={() => setOpen(false)}
                className={({ isActive }) => `px-3 py-2 text-sm rounded-lg ${isActive ? "text-brand-purple bg-brand-purple/10 font-semibold" : "text-slate-600"}`}>
                {n.label}
              </NavLink>
            ))}
          </div>
          <div className="container-x pb-4 flex flex-col gap-2">
            {nav.second_opinion_btn !== false && <Link to="/second-opinion" onClick={() => setOpen(false)} className="btn-outline text-sm justify-center" data-testid="mobile-second-opinion-btn">Get Second Opinion</Link>}
            {nav.book_btn !== false && <Link to="/book-appointment" onClick={() => setOpen(false)} className="btn-primary text-sm justify-center" data-testid="mobile-book-appointment-btn">Book Appointment</Link>}
          </div>
        </div>
      )}
    </header>
  );
}
